import {
  Animated,
  Easing,
  ImageBackground,
  Platform,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  Info,
  X,
} from "lucide-react-native";
import { Circle, Svg } from "react-native-svg";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { IconButton } from "../primitives/IconButton";
import { TextActionButton } from "../primitives/TextActionButton";
import {
  radii,
  semanticColors,
  shadows,
  spacing,
  typography,
} from "../theme/tokens";
import { useReducedMotion } from "./useReducedMotion";

export type ToastOptions = {
  message: string;
  /**
   * What the toast tells her: something didn't work (`error`), something
   * worked (`success`), or a plain note (`info`). Each has its own icon.
   */
  variant?: "error" | "success" | "info";
  duration?: number;
  /** Brief upward entrance and fade-out for a completed action. */
  motion?: "rise";
  /** One follow-up the message invites, such as reviewing what it names. */
  action?: {
    label: string;
    onPress: () => void;
    /** Stable analytics identifier; the label is translated copy. */
    analyticsAction: string;
  };
};

type ToastState = ToastOptions & { id: number };

type ToastContextValue = {
  showToast: (options: ToastOptions) => void;
  dismissToast: () => void;
};

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
  dismissToast: () => {},
});

/**
 * Where the toast is drawn. A dialog is a native modal (on the web, a layer
 * above the page), so a toast drawn at the app's root sits under its
 * backdrop. Each open dialog or sheet mounts a `ToastHost`, and the toast is
 * drawn in the newest one; with none open, at the root.
 */
type ToastHostContextValue = {
  toast: ToastState | null;
  hosts: object[];
  registerHost: (host: object) => () => void;
  dismissToast: () => void;
  completeToast: (id: number) => void;
  backgroundSource: ImageSourcePropType;
};

const ToastHostContext = createContext<ToastHostContextValue | null>(null);

const TOAST_DURATION = 6000;
const CLOSE_RING_RADIUS = 9;
const CLOSE_RING_CIRCUMFERENCE = 2 * Math.PI * CLOSE_RING_RADIUS;
const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const TOAST_ICON = { error: CircleAlert, success: CircleCheck, info: Info };

function ToastViewport({
  toast,
  onDismiss,
  onAutoDismiss,
  backgroundSource,
}: {
  toast: ToastState | null;
  onDismiss: () => void;
  onAutoDismiss: (id: number) => void;
  backgroundSource: ImageSourcePropType;
}) {
  const insets = useSafeAreaInsets();
  const progress = useRef(new Animated.Value(0)).current;
  const motionProgress = useRef(new Animated.Value(1)).current;
  const reducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    motionProgress.stopAnimation();
    if (!toast || toast.motion !== "rise") {
      motionProgress.setValue(1);
      return;
    }

    const duration = toast.duration ?? TOAST_DURATION;
    if (reducedMotion) {
      motionProgress.setValue(1);
      const timer = setTimeout(() => onAutoDismiss(toast.id), duration);
      return () => clearTimeout(timer);
    }

    const transitionDuration = Math.min(220, duration / 2);
    motionProgress.setValue(0);
    const animation = Animated.sequence([
      Animated.timing(motionProgress, {
        toValue: 1,
        duration: transitionDuration,
        easing: Easing.out(Easing.quad),
        useNativeDriver: Platform.OS !== "web",
      }),
      Animated.delay(Math.max(0, duration - transitionDuration * 2)),
      Animated.timing(motionProgress, {
        toValue: 2,
        duration: transitionDuration,
        easing: Easing.in(Easing.quad),
        useNativeDriver: Platform.OS !== "web",
      }),
    ]);
    // Let the exit finish before removing the toast, even when a busy frame
    // delays the entrance. A separate provider timer can cut this sequence off.
    animation.start(({ finished }) => {
      if (finished) onAutoDismiss(toast.id);
    });
    return () => animation.stop();
  }, [motionProgress, onAutoDismiss, reducedMotion, toast]);

  useEffect(() => {
    progress.stopAnimation();
    if (!toast) {
      progress.setValue(0);
      return;
    }

    progress.setValue(1);
    const animation = Animated.timing(progress, {
      toValue: 0,
      duration: toast.duration ?? TOAST_DURATION,
      useNativeDriver: false,
    });
    animation.start();

    return () => animation.stop();
  }, [progress, toast]);

  if (!toast) return null;
  const Icon = TOAST_ICON[toast.variant ?? "info"];

  return (
    <Animated.View
      testID="toast-notification"
      pointerEvents="box-none"
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        left: 0,
        zIndex: 1000,
        elevation: 1000,
        paddingTop: insets.top + spacing.sm,
        paddingHorizontal: spacing.lg,
        alignItems: "center",
        opacity: motionProgress.interpolate({
          inputRange: [0, 1, 2],
          outputRange: [0, 1, 0],
          extrapolate: "clamp",
        }),
        transform: [
          {
            translateY: motionProgress.interpolate({
              inputRange: [0, 1, 2],
              outputRange: [12, 0, -8],
              extrapolate: "clamp",
            }),
          },
        ],
      }}
    >
      <ImageBackground
        source={backgroundSource}
        resizeMode="cover"
        imageStyle={{ borderRadius: radii.lg }}
        style={{
          maxWidth: 520,
          borderRadius: radii.lg,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.16)",
          ...shadows.lg,
        }}
      >
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            // A confirmation reads as good news at a glance: the same
            // texture, tinted the success green.
            backgroundColor:
              toast.variant === "success"
                ? semanticColors.status.successLayer
                : "rgba(21, 10, 7, 0.1)",
          }}
        />
        <View style={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton
            accessibilityLabel="Dismiss notification"
            onPress={onDismiss}
            size="sm"
            surface="soft"
            backgroundColor={semanticColors.onDark.fill.subtle}
            pressedBackgroundColor={semanticColors.onDark.fill.strong}
          >
            <View
              style={{
                width: 22,
                height: 22,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Svg
                pointerEvents="none"
                width={22}
                height={22}
                viewBox="0 0 22 22"
                style={{ position: "absolute" }}
              >
                <Circle
                  cx={11}
                  cy={11}
                  r={CLOSE_RING_RADIUS}
                  fill="none"
                  stroke={semanticColors.onDark.fill.strong}
                  strokeWidth={1.5}
                />
                <AnimatedCircle
                  cx={11}
                  cy={11}
                  r={CLOSE_RING_RADIUS}
                  fill="none"
                  stroke={semanticColors.onDark.text.primary}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeDasharray={[
                    CLOSE_RING_CIRCUMFERENCE,
                    CLOSE_RING_CIRCUMFERENCE,
                  ]}
                  strokeDashoffset={progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [CLOSE_RING_CIRCUMFERENCE, 0],
                  })}
                />
              </Svg>
              <X
                size={14}
                color={semanticColors.onDark.text.secondary}
                strokeWidth={1.8}
              />
            </View>
          </IconButton>
        </View>
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{
            gap: spacing.sm,
            padding: spacing.md,
            paddingRight: spacing.md + 30,
          }}
        >
          <View
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
          >
            <View style={{ paddingTop: 1 }}>
              <Icon
                size={17}
                color={semanticColors.onDark.text.primary}
                strokeWidth={1.8}
              />
            </View>
            <Text
              style={{
                flex: 1,
                fontFamily: typography.families.sans,
                fontSize: typography.sizes.base,
                lineHeight: typography.sizes.base * 1.45,
                color: semanticColors.onDark.text.primary,
              }}
            >
              {toast.message}
            </Text>
          </View>
          {toast.action ? (
            <TextActionButton
              label={toast.action.label}
              action={toast.action.analyticsAction}
              tone="onDark"
              size="sm"
              icon={ArrowRight}
              iconPosition="trailing"
              onPress={() => {
                const followUp = toast.action?.onPress;
                onDismiss();
                followUp?.();
              }}
              style={{ alignSelf: "flex-start" }}
            />
          ) : null}
        </View>
      </ImageBackground>
    </Animated.View>
  );
}

export function ToastProvider({
  children,
  backgroundSource,
}: {
  children: ReactNode;
  backgroundSource: ImageSourcePropType;
}) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const nextIdRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismissToast = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setToast(null);
  }, []);

  const showToast = useCallback((options: ToastOptions) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;

    const id = nextIdRef.current + 1;
    nextIdRef.current = id;
    setToast({ ...options, id });

    if (options.motion !== "rise") {
      const duration = options.duration ?? TOAST_DURATION;
      timeoutRef.current = setTimeout(() => {
        setToast((current) => (current?.id === id ? null : current));
        timeoutRef.current = null;
      }, duration);
    }
  }, []);

  const completeToast = useCallback((id: number) => {
    setToast((current) => (current?.id === id ? null : current));
  }, []);

  const [hosts, setHosts] = useState<object[]>([]);
  const registerHost = useCallback((host: object) => {
    setHosts((current) => [...current, host]);
    return () => setHosts((current) => current.filter((h) => h !== host));
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const toastValue = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );
  const hostValue = useMemo(
    () => ({
      toast,
      hosts,
      registerHost,
      dismissToast,
      completeToast,
      backgroundSource,
    }),
    [toast, hosts, registerHost, dismissToast, completeToast, backgroundSource],
  );

  return (
    <ToastContext.Provider value={toastValue}>
      <ToastHostContext.Provider value={hostValue}>
        <View style={{ flex: 1 }}>
          {children}
          {hosts.length === 0 ? (
            <ToastViewport
              toast={toast}
              onDismiss={dismissToast}
              onAutoDismiss={completeToast}
              backgroundSource={backgroundSource}
            />
          ) : null}
        </View>
      </ToastHostContext.Provider>
    </ToastContext.Provider>
  );
}

/**
 * Draws the toast inside a modal layer. Render it as the last child of a
 * native `Modal`, only while the modal is open; `Dialog` and `DetailSheet`
 * already do.
 */
export function ToastHost() {
  const context = useContext(ToastHostContext);
  const [host] = useState(() => ({}));
  const registerHost = context?.registerHost;

  useLayoutEffect(() => registerHost?.(host), [registerHost, host]);

  if (!context || context.hosts[context.hosts.length - 1] !== host) {
    return null;
  }
  return (
    <ToastViewport
      toast={context.toast}
      onDismiss={context.dismissToast}
      onAutoDismiss={context.completeToast}
      backgroundSource={context.backgroundSource}
    />
  );
}

export function useToast() {
  return useContext(ToastContext);
}
